import React from 'react';
import './MetricsStrip.css';

/** Rings + glyphs scaled down */
const RING = 76; /* 84 * 0.9 */
const STROKE = 3.6;

function MetricRing({ color, fillPct, children }) {
    const r = (RING - STROKE) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, Number(fillPct) || 0));
    const offset = c * (1 - pct / 100);
    const hasProgress = pct > 0;

    return (
        <div className="dv2-metrics__ring" style={{ color }}>
            <svg
                width={RING}
                height={RING}
                viewBox={`0 0 ${RING} ${RING}`}
                aria-hidden
            >
                <circle
                    cx={RING / 2}
                    cy={RING / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.5"
                    strokeWidth={STROKE}
                />
                {hasProgress && (
                    <circle
                        cx={RING / 2}
                        cy={RING / 2}
                        r={r}
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity="1"
                        strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${c} ${c}`}
                        strokeDashoffset={offset}
                        transform={`rotate(-90 ${RING / 2} ${RING / 2})`}
                    />
                )}
            </svg>
            <span className="dv2-metrics__glyph">{children}</span>
        </div>
    );
}

const IconCheck = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const IconFlag = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
);

const IconFlame = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
);

const IconStar = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

function formatDays(count) {
    const n = Number(count);
    if (Number.isNaN(n)) return '—';
    return `${n} ${n === 1 ? 'day' : 'days'}`;
}

const MetricsStrip = ({ progress, loading }) => {
    const steps = progress?.stepsTaken;
    const goals = progress?.goalsCompleted;
    const current = progress?.currentStreak;
    const longest = progress?.longestStreak;

    const items = [
        {
            key: 'steps',
            label: 'Action Steps Taken',
            value: loading || steps == null ? '—' : String(steps),
            color: '#7EC8F0',
            fillPct: !loading && Number(steps) > 0 ? 78 : 0,
            icon: <IconCheck />,
        },
        {
            key: 'goals',
            label: 'Goals Completed',
            value: loading || goals == null ? '—' : String(goals),
            color: '#6BCF8A',
            fillPct: !loading && Number(goals) > 0 ? 100 : 0,
            icon: <IconFlag />,
        },
        {
            key: 'current',
            label: 'Current Streak',
            value: loading || current == null ? '—' : formatDays(current),
            color: '#E8924A',
            fillPct: !loading && Number(current) > 0 ? 72 : 0,
            icon: <IconFlame />,
        },
        {
            key: 'longest',
            label: 'Longest Streak',
            value: loading || longest == null ? '—' : formatDays(longest),
            color: '#B794F6',
            fillPct: !loading && Number(longest) > 0 ? 78 : 0,
            icon: <IconStar />,
        },
    ];

    return (
        <section className="dv2-metrics dv2-glass" aria-label="Progress">
            {items.map((item) => (
                <div
                    key={item.key}
                    className="dv2-metrics__item"
                    style={{ '--dv2-metric-accent': item.color }}
                >
                    <MetricRing color={item.color} fillPct={item.fillPct}>
                        {item.icon}
                    </MetricRing>
                    <div className="dv2-metrics__text">
                        <span className="dv2-metrics__label">{item.label}</span>
                        <span className="dv2-metrics__value">{item.value}</span>
                    </div>
                </div>
            ))}
        </section>
    );
};

export default MetricsStrip;
